/**
 * Build the standard import workbooks in-browser from confirmed wizard decisions.
 *
 * The ClosedXML reader matches by sheet name + header name (order/case/`*`-insensitive)
 * and ignores the read-only `ID`/`Kundennummer` columns, so we omit them entirely —
 * absent ID means "create". The task workbook always carries BOTH `Aufgaben` and
 * `Zeitregeln` sheets, as the task import reads both unconditionally. Each builder
 * returns a `File` named `*.xlsx` so the upload gate (extension check) is satisfied.
 */

import * as XLSX from 'xlsx';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export interface CustomerRowOut {
  name: string;
  street: string;
  plz: string;
  ort: string;
  land: string;
  vatId?: string;
  status?: 'Aktiv' | 'Inaktiv';
  externalNotes?: string;
  internalNotes?: string;
}

export interface ObjectRowOut {
  /** Kundennummer (preferred) or unambiguous customer name. */
  kunde: string;
  objektname: string;
  street: string;
  plz: string;
  ort: string;
  land: string;
  status?: 'Aktiv' | 'Inaktiv';
  externalNotes?: string;
}

export interface TaskRowOut {
  /** `"K-2025-001 — Objektname"`. */
  objekt: string;
  dienstleistung: string;
  bezeichnung?: string;
  beschreibung?: string;
  dauer: number;
  aktiv: 'Ja' | 'Nein';
}

export interface RuleRowOut {
  /** `"<objektlabel> · <service>"`. */
  aufgabe: string;
  typ: string;
  wochentage?: string;
  wochentag?: string;
  ordnungszahl?: string;
  von?: string;
  bis?: string;
  aktiv: 'Ja' | 'Nein';
}

const KUNDEN_HEADERS = ['Name *', 'Straße *', 'PLZ *', 'Ort *', 'Land *', 'UID-Nummer', 'Status *', 'Notizen extern', 'Notizen intern'];
const OBJEKTE_HEADERS = ['Kunde *', 'Objektname *', 'Straße *', 'PLZ *', 'Ort *', 'Land *', 'Ansprechpartner', 'Breitengrad', 'Längengrad', 'Reihenfolge', 'Status *', 'Notizen extern', 'Notizen intern'];
const AUFGABEN_HEADERS = ['Objekt *', 'Dienstleistung *', 'Bezeichnung', 'Beschreibung', 'Dauer (Min) *', 'Anleitung', 'Material', 'Aktiv *'];
const ZEITREGELN_HEADERS = ['Aufgabe *', 'Typ *', 'Wochentage', 'Wochentag', 'Ordnungszahl', 'Fenster von', 'Fenster bis', 'Aktiv *'];

function sheet(headers: string[], rows: (string | number)[][]): XLSX.WorkSheet {
  return XLSX.utils.aoa_to_sheet([headers, ...rows]);
}

function toFile(wb: XLSX.WorkBook, name: string): File {
  const out = XLSX.write(wb, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer;
  return new File([out], name, { type: XLSX_MIME });
}

export function buildKundenFile(rows: CustomerRowOut[]): File {
  const data = rows.map((r) => [
    r.name, r.street, r.plz, r.ort, r.land, r.vatId ?? '', r.status ?? 'Aktiv', r.externalNotes ?? '', r.internalNotes ?? '',
  ]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet(KUNDEN_HEADERS, data), 'Kunden');
  return toFile(wb, 'Kunden-Import.xlsx');
}

export function buildObjekteFile(rows: ObjectRowOut[]): File {
  const data = rows.map((r) => [
    r.kunde, r.objektname, r.street, r.plz, r.ort, r.land, '', '', '', '', r.status ?? 'Aktiv', r.externalNotes ?? '', '',
  ]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet(OBJEKTE_HEADERS, data), 'Objekte');
  return toFile(wb, 'Objekte-Import.xlsx');
}

export function buildAufgabenFile(tasks: TaskRowOut[], rules: RuleRowOut[]): File {
  const taskData = tasks.map((t) => [
    t.objekt, t.dienstleistung, t.bezeichnung ?? '', t.beschreibung ?? '', t.dauer, '', '', t.aktiv,
  ]);
  const ruleData = rules.map((r) => [
    r.aufgabe, r.typ, r.wochentage ?? '', r.wochentag ?? '', r.ordnungszahl ?? '', r.von ?? '', r.bis ?? '', r.aktiv,
  ]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet(AUFGABEN_HEADERS, taskData), 'Aufgaben');
  XLSX.utils.book_append_sheet(wb, sheet(ZEITREGELN_HEADERS, ruleData), 'Zeitregeln');
  return toFile(wb, 'Aufgaben-Import.xlsx');
}
